// Option C (2026-10-11): the per-fund FundSignalPanel configs, referenced
// from the fund page registry (FUND_PAGES[slug].signalPanel). Every value
// here is what GoldSignalPanel.vue / GgeSignalPanel.vue used to hard-code.
// Gold NAV figures are PENCE (feedback_gold_nav_pence) — the panel formats
// every NAV/K_t figure as "<n>p" for both funds.
import { markRaw } from 'vue'
import { api } from '../../api/client'
import { useGoldLiveSynthNav, useGgeLiveSynthNav } from '../../composables/useLiveSynthNav'
import { deriveGoldOverlay } from '../../utils/goldOverlay'
import GoldSignalExplainer from './GoldSignalExplainer.vue'
import GgeSignalExplainer from './GgeSignalExplainer.vue'
import { bps, fmtTimestamp, fmtUsd, type FundSignalPanelConfig } from './fundSignalPanel'
import { goldGateFields, gateStateText, ggeClPrint } from './fundSignalFields'

// gold-productionization Task 10: /api/signal/gold — a thin Redis read of the
// payload the Python gold_mt_signal producer publishes at ~08:40
// Europe/London (post 2026-07-26 clock correction — matches BGA/JH).
export const GOLD_SIGNAL_PANEL: FundSignalPanelConfig = {
  key: 'gold',
  title: 'Gold Signal',
  rootClass: 'gold-signal',
  errorName: 'Gold',
  awaitName: 'gold',
  redisKey: 'latest:signal:gold',
  accent: '#f6c542',
  explainerAccent: '#c9a227',
  fetchSignal: () => api.goldSignal(),
  momentumField: 'm_1040',
  momentumSub: '10:40',
  momentumExplainerKey: 'm1040',
  futuresSymbol: 'GC',
  previewShowsGate: true,
  // Ninety One Global Gold closes on days the UK calendar does not
  // (Config/dealing_calendars/ninety_one_gold.json) — count down to the
  // next gold DEALING day.
  dealingCalendarKey: 'gold',
  fetchKtLatest: () => api.goldKtLatest(),
  telemetryTestIdSuffix: 'kt-figure',
  useLiveSynthNav: () => useGoldLiveSynthNav(),
  strikeAnchorFund: 'gold',
  // Gold K_t live overlay bridge (2026-08-31/09-01): beta·Δgold from GC since
  // the 16:00 NY anchor — deriveGoldOverlay is the single source of truth
  // shared with GoldSparklineBar and HoldingsPanel's footer.
  overlay: { derive: deriveGoldOverlay, assetLabel: 'gold' },
  decisionReasons: {
    m_ge_threshold_and_gate_open: 'momentum at/above the BUY threshold and the yield gate open → BUY',
    m_below_threshold: 'momentum below the BUY threshold → SELL (flat)',
    gate_closed: 'momentum passed but the yield-collapse gate is closed → SELL (flat)',
    m_unavailable_or_stale: 'no fresh GC print at decision time → degraded HOLD, prior position kept via the position proxy',
  },
  extraStats: (v) => {
    const g = goldGateFields(v.signal)
    return [
      { label: 'Δy', labelSub: '20', info: { key: 'dy20', testid: 'gold-info-dy', aria: 'Explain Δy20' },
        value: bps(g.dyBps), valueTestId: 'gold-dy-bps',
        sub: `drop gate ${g.dropBps != null ? g.dropBps.toFixed(0) + ' bps' : '—'}` },
      { label: 'Yield gate', info: { key: 'gate', testid: 'gold-info-gate', aria: 'Explain yield gate' },
        value: gateStateText(g.gateOpen), valueTestId: 'gold-gate-state',
        valueClass: g.gateOpen === false ? 'gate-blocked' : 'gate-open',
        sub: `mode ${g.gateMode ?? '—'}` },
    ]
  },
  explainer: markRaw(GoldSignalExplainer),
}

// GGE promotion Phase 4 (2026-08-18): the WS Guinness Global Energy decision
// view — gold's contract minus the yield gate (the validated tune has none).
// CL (WTI) momentum from the prior 16:00 America/New_York close to the last
// print before 08:40 London — exactly what the fund's 12:00-UK NAV strike has
// not yet absorbed (~65% North-American book valued at T−1 closes).
export const GGE_SIGNAL_PANEL: FundSignalPanelConfig = {
  key: 'gge',
  title: 'GGE Signal',
  rootClass: 'gge-signal',
  errorName: 'GGE',
  awaitName: 'GGE',
  redisKey: 'latest:signal:gge',
  accent: '#4fb98a',
  explainerAccent: '#2f9e6e',
  fetchSignal: () => api.ggeSignal(),
  momentumField: 'm_0840',
  momentumSub: '08:40',
  momentumExplainerKey: 'm0840',
  futuresSymbol: 'CL',
  previewShowsGate: false,
  fetchKtLatest: () => api.ggeKtLatest(),
  // Official NAV (pence, HL factsheet convention) beside K_t in the telemetry.
  fetchNavLatest: () => api.ggeNav(),
  telemetryTestIdSuffix: 'nav-figure',
  useLiveSynthNav: () => useGgeLiveSynthNav(),
  strikeAnchorFund: 'gge',
  // Crude contract-roll guard (src/signal_core/gge_mt_signal.py): only these
  // two m_reason values get a pill beside the degraded badge.
  degradedPills: {
    contract_roll: {
      text: 'CRUDE ROLL',
      title: 'Overnight crude move spans a futures contract change — signal degraded, prior position carried',
      testid: 'gge-crude-roll-pill',
    },
    contract_unknown: {
      text: 'CL UNKNOWN',
      title: 'A crude bar has no contract id (cold start after deploy or historical fill) — signal degraded, prior position carried',
      testid: 'gge-cl-unknown-pill',
    },
  },
  decisionReasons: {
    m_ge_threshold: 'CL momentum at/above the BUY threshold → BUY',
    m_below_threshold: 'CL momentum below the BUY threshold → SELL (flat)',
    m_unavailable_or_stale: 'no fresh CL print at decision time → degraded HOLD, prior position kept via the position proxy',
  },
  extraStats: (v) => {
    const cl = ggeClPrint(v.signal)
    return [
      { label: 'CL print', info: { key: 'clprint', testid: 'gge-info-cl', aria: 'Explain CL print' },
        value: fmtUsd(cl?.price), valueTestId: 'gge-cl-print',
        sub: cl?.ts ? fmtTimestamp(cl.ts) + ' UK' : '—' },
      { label: '16:00-NY anchor',
        value: fmtUsd(cl?.anchor), valueTestId: 'gge-cl-anchor',
        sub: cl?.anchorTs ? fmtTimestamp(cl.anchorTs) + ' UK' : '—' },
    ]
  },
  explainer: markRaw(GgeSignalExplainer),
}
